import React, { useState, useEffect, useRef } from "react";
import { Bell } from "lucide-react";
import { useNotificationCenter } from "../../contexts/NotificationContext";

interface NotificationBellProps {
  onNotificationClick?: () => void;
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  onNotificationClick,
  className = "",
}) => {
  const { unreadCount, loadUnreadCount } = useNotificationCenter();
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevUnreadCount = useRef(unreadCount);

  // Fetch unread count when bell is shown
  useEffect(() => {
    if (loadUnreadCount) {
      loadUnreadCount();
    }
  }, [loadUnreadCount]);

  // Trigger ring animation whenever new notifications arrive
  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 1000);
      return () => clearTimeout(timer);
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);

  return (
    <button
      onClick={onNotificationClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 ${className}`}
      title="Notifications"
      aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
    >
      {/* Bell Icon */}
      <Bell
        className={`w-5 h-5 transition-transform duration-200 ${
          isHovered ? "scale-110" : "scale-100"
        } ${isAnimating ? "animate-bell-ring" : ""}`}
      />

      {/* Unread Badge — shows count, max "9+" */}
      {unreadCount > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-0.5
            bg-red-500 text-white text-[10px] font-bold rounded-full
            flex items-center justify-center shadow-md
            ring-2 ring-white
            animate-badge-pop"
          key={unreadCount} /* re-mounts on change to re-trigger animation */
        >
          {unreadCount > 99 ? "99+" : unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}

      {/* Pulse ring — shows only when there are unread notifications */}
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-400 opacity-60 animate-ping pointer-events-none" />
      )}
    </button>
  );
};

export default NotificationBell;
